import React, { useContext, useEffect, useRef, useState } from "react";
import { Col, Row } from "reactstrap";
import { toast } from "react-toastify";
import _ from "lodash";
import {
   CustomerTypeahead, FilterDateTime, FilterSelect, FilterText,
   PackingInquiryInvoiceGrid, ServiceThreadSpinner,
} from ".";
import { DesktopContainer } from "../layouts";
import { api, constants, filterHelpers, helpers, UserContext } from "../utils";

export default function InvoiceAndPackingList({ short, ...props }) {
   const userCtx = useContext(UserContext);
   const isCustomer = userCtx && userCtx.currentUser && userCtx.currentUser.isAnyCustomerTypeRole;
   const [billToCityList, setBillToCityList] = useState([]);
   const [customer, setCustomer] = useState();
   const [customerItems, setCustomerItems] = useState([]);
   const [data, setData] = useState([]);
   const [downloading, setDownloading] = useState(false);
   const [emptyCriteria, setEmptyCriteria] = useState(false);
   const [initialized, setInitialized] = useState(false);
   const [loaded, setLoaded] = useState(false);
   const [page, setPage] = useState(1);
   const [pageSize, setPageSize] = useState(25);
   const [salespeopleList, setSalespeopleList] = useState([]);
   const [shipToCityList, setShipToCityList] = useState([]);
   const [sortDirection, setSortDirection] = useState("desc");
   const [sortField, setSortField] = useState("documentDate");
   const [totalPages, setTotalPages] = useState();
   const [totals, setTotals] = useState();
   const [filters, setFilters] = useState([
      { filterName: "StartDate", value: null },
      { filterName: "EndDate", value: null },
      { filterName: "OrderType", value: 0 },
   ]);
   // Live search fires a query on every filter change; this tracks the most recent
   // request so a slower earlier response (e.g. a partial item number) can't land
   // after and overwrite the latest results with stale/empty data.
   const latestRequest = useRef(0);

   useEffect(() => {
      document.title = `Invoices & Packing Lists`;
      if (!initialized && userCtx) {
         if (userCtx.currentUser && userCtx.currentUser.isAnyCustomerTypeRole) {
            const activeId = helpers.activeCustomerId(userCtx);
            setCustomer({
               label: activeId,
               value: activeId,
            });
            api
               .fetch(`Customer/LoadIplDropdowns`)
               .then((r) => {
                  if (r.data.success) {
                     setShipToCityList(
                        _.map(r.data.shipToCities, (city) => {
                           return { value: city.ddlCityValue, label: city.ddlCityText };
                        })
                     );
                     setCustomerItems(
                        _.map(r.data.customerItems, (item) => {
                           return { value: item.ddlValue, label: item.ddlText };
                        })
                     );
                  }
               })
               .catch(api.catchHandler)
               .finally(() => {
                  setInitialized(true);
               });
         } else {
            setCustomer({
               label: userCtx.customerFilter?.label ? userCtx.customerFilter.label : userCtx.customerFilter ? userCtx.customerFilter : "",
               value: userCtx.customerFilter?.value ? userCtx.customerFilter.value : userCtx.customerFilter ? userCtx.customerFilter : "",
            });
            api
               .fetch(`Customer/LoadIplDropdowns`)
               .then((r) => {
                  if (r.data.success) {
                     setSalespeopleList(
                        _.map(r.data.salespeople, (sp) => {
                           return { value: sp.salespersonId, label: sp.salespersonText };
                        })
                     );
                     setBillToCityList(
                        _.map(r.data.billToCities, (city) => {
                           return { value: city.ddlCityValue, label: city.ddlCityText };
                        })
                     );
                     setShipToCityList(
                        _.map(r.data.shipToCities, (city) => {
                           return { value: city.ddlCityValue, label: city.ddlCityText };
                        })
                     );

                     if (userCtx.currentUser && userCtx.currentUser.isSalesperson) {
                        const filtersCopy = _.slice(filters);
                        filtersCopy.push({
                           filterName: "SalespersonId",
                           value: r.data.salespeople[0].salespersonValue,
                        });
                        setFilters(filtersCopy);
                     }
                  }
               })
               .catch(api.catchHandler)
               .finally(() => {
                  setInitialized(true);
               });
         }
      }
      if (initialized && !loaded) {
         runQuery();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [initialized, loaded]);

   function createPayload() {
      let payload = {
         CustomerId: _.isNil(customer) ? "" : _.trim(customer.value),
         sortField: sortField,
         sortDirection: sortDirection,
         pageNumber: page,
         pageSize: pageSize,
      };
      _.map(filters, (filter) => (payload[filter.filterName] = filter.value));
      return payload;
   }

   // function exportPdf() {
   //   setDownloading(true)
   //   const payload = createPayload()
   //   api
   //     .postDownloadBinary("Report/ShipmentInquiry",
   //       payload,
   //       constants.MIME_TYPES.pdf,
   //       `ServiceThread_Shipment_Inquiry_${payload.CustomerId}_` + dateHelpers.getDateStringForReportName() + ".pdf")
   //     .catch((error) => {
   //       api.catchHandler(error)
   //       toast.error("There was an error printing this report");
   //     })
   //     .finally(() => setDownloading(false));
   // }

   function exportXlsx() {
      setDownloading(true);
      const payload = createPayload();

      api
         .postGetBlob("Customer/ExportCustomerInvoicesXLSX", payload)
         .then((r) => {
            if (r.data.success === false) {
               if (_.isString(r.data.message)) {
                  toast.error(r.data.message);
               }
               return;
            }
            if (r.data) {
               const XLSX_FILENAME = payload.CustomerId ? `${payload.CustomerId}_Invoice_History.xlsx` : `Invoice_History.xlsx`;
               helpers.downloadExport(
                  r.data,
                  XLSX_FILENAME,
                  constants.MIME_TYPES.xlsx
               );
            } else {
               toast.error(r.message || "There was an error downloading the invoice history");
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error("There was an error downloading the invoice history");
         })
         .finally(() => setDownloading(false));
   }

   function onFilterChange(changedFilter) {
      const filterChanges = filterHelpers.getFilters(filters, changedFilter);
      setFilters(filterChanges);
      setTotalPages(null);
      onPageChange(1);
   }

   function onPageChange(page) {
      setPage(page);
      setLoaded(false);
   }

   function onPageSizeChange(size) {
      setPageSize(size);
      setTotalPages(null);
      onPageChange(1);
   }

   function onSortChange(field, direction) {
      setSortField(field);
      setSortDirection(direction);
      onPageChange(1);
   }

   function runQuery() {
      var payload = createPayload();
      if (
         !payload.BTCity &&
         !payload.CustomerId &&
         !payload.EndDate &&
         !payload.StartDate &&
         !payload.OrderType &&
         !payload.ItemNumber &&
         !payload.LotNumber &&
         !payload.OrderId &&
         !payload.STCity &&
         !payload.SalespersonId &&
         !payload.customerPONumber
      ) {
         setEmptyCriteria(true);
         setLoaded(true);
         setData([]);
         setTotalPages(null);
         setTotals(null);
         return;
      }
      setEmptyCriteria(false);
      const requestId = ++latestRequest.current;
      api.post("Customer/GetPaginatedCustomerInvoices", payload).then((response) => {
         if (requestId !== latestRequest.current) return; // superseded by a newer query — ignore
         if (response && response.data) {
            setData(response.data.invoices.items);
            setTotalPages(_.ceil(response.data.invoices.totalCount / pageSize));
            setTotals(response.data.totals);
         }
         setLoaded(true);
      }).catch(api.catchHandler);
   }


   if (short) {
      return (
         <>
            <Row className="justify-content-center mb-3">
               <Col xs={5}>
                  {!isCustomer && (
                     <>
                        <Row className="align-items-center mt-2">
                           <Col xs={3}>
                              <h5 children="Customer:" className="float-end " />
                           </Col>
                           <Col xs={9}>
                              <CustomerTypeahead
                                 value={customer}
                                 onChange={(e) => {
                                    setCustomer(e);
                                    setPage(1);
                                    setLoaded(false);
                                 }}
                              />
                           </Col>
                        </Row>
                        <Row className="align-items-center mt-2">
                           <Col xs={3}>
                              <h5 children="Salesperson:" className="float-end " />
                           </Col>
                           <Col xs={9}>
                              <FilterSelect
                                 filterName="SalespersonId"
                                 options={salespeopleList}
                                 value={filterHelpers.getValue(filters, "SalespersonId")}
                                 onChangeCallback={(e) => onFilterChange(e)}
                                 isClearable={
                                    userCtx.currentUser &&
                                    (userCtx.currentUser.isAdmin ||
                                       userCtx.currentUser.isPowerUser ||
                                       userCtx.currentUser.isCSRRole ||
                                       userCtx.currentUser.isSalesRole)
                                 }
                              />
                           </Col>
                        </Row>
                     </>
                  )}
                  <Row className="align-items-center mt-2">
                     <Col xs={3}>
                        <h5 children="Invoice:" className="float-end " />
                     </Col>
                     <Col xs={9}>
                        <FilterText
                           customFilterClassName="p-0"
                           filterName="OrderId"
                           value={filterHelpers.getValue(filters, "OrderId")}
                           onChangeCallback={onFilterChange}
                        />
                     </Col>
                  </Row>
                  <Row className="align-items-center mt-2">
                     <Col xs={3}>
                        <h5 children="PO:" className="float-end " />
                     </Col>
                     <Col xs={9}>
                        <FilterText
                           customFilterClassName="p-0"
                           filterName="customerPONumber"
                           value={filterHelpers.getValue(filters, "customerPONumber")}
                           onChangeCallback={onFilterChange}
                        />
                     </Col>
                  </Row>
                  <Row className="align-items-center mt-2">
                     <Col xs={3}>
                        <h5 children="Case:" className="float-end " />
                     </Col>
                     <Col xs={9}>
                        <FilterText
                           customFilterClassName="p-0"
                           filterName="LotNumber"
                           value={filterHelpers.getValue(filters, "LotNumber")}
                           onChangeCallback={onFilterChange}
                        />
                     </Col>
                  </Row>
                  <Row className="align-items-center mt-2">
                     <Col xs={3}>
                        <h5 children="Item:" className="float-end " />
                     </Col>
                     <Col xs={9}>
                        {!isCustomer ? (
                           <FilterText
                              customFilterClassName="p-0"
                              filterName="ItemNumber"
                              value={filterHelpers.getValue(filters, "ItemNumber")}
                              onChangeCallback={onFilterChange}
                           />
                        ) : (
                           <FilterSelect
                              filterName="ItemNumber"
                              options={customerItems}
                              value={filterHelpers.getValue(filters, "ItemNumber")}
                              onChangeCallback={onFilterChange}
                              isClearable
                           />
                        )}
                     </Col>
                  </Row>
               </Col>
               <Col xs={5}>
                  {!isCustomer && (
                     <Row className="align-items-center mt-2">
                        <Col xs={3}>
                           <h5 children="Bill To City:" className="float-end " />
                        </Col>
                        <Col xs={9}>
                           <FilterSelect
                              filterName="BTCity"
                              options={billToCityList}
                              value={filterHelpers.getValue(filters, "BTCity")}
                              onChangeCallback={onFilterChange}
                              isClearable
                           />
                        </Col>
                     </Row>
                  )}
                  <Row className="align-items-center mt-2">
                     <Col xs={3}>
                        <h5 children="Ship To City:" className="float-end " />
                     </Col>
                     <Col xs={9}>
                        <FilterSelect
                           filterName="STCity"
                           options={shipToCityList}
                           value={filterHelpers.getValue(filters, "STCity")}
                           onChangeCallback={onFilterChange}
                           isClearable
                        />
                     </Col>
                  </Row>
                  <Row className="align-items-center mt-2">
                     <Col xs={3}>
                        <h5 children="Start Ship Date:" className="float-end " />
                     </Col>
                     <Col xs={9}>
                        <FilterDateTime
                           filterName="StartDate"
                           value={filterHelpers.getValue(filters, "StartDate")}
                           onChangeCallback={onFilterChange}
                        />
                     </Col>
                  </Row>
                  <Row className="align-items-center mt-2">
                     <Col xs={3}>
                        <h5 children="End Ship Date:" className="float-end " />
                     </Col>
                     <Col xs={9}>
                        <FilterDateTime
                           filterName="EndDate"
                           value={filterHelpers.getValue(filters, "EndDate")}
                           onChangeCallback={onFilterChange}
                        />
                     </Col>
                  </Row>
                  <Row className="align-items-center mt-2">
                     <Col xs={3}>
                        <h5 children="Order Type:" className="float-end " />
                     </Col>

                     <Col xs={9}>
                        <FilterSelect
                           filterName="OrderType"
                           options={constants.ORDER_TYPES}
                           value={filterHelpers.getValue(filters, "OrderType")}
                           onChangeCallback={onFilterChange}
                           isClearable={false}
                        />
                     </Col>
                  </Row>
               </Col>
            </Row>
            <Row className="mb-5">
               <Col xxl={{ size: 9, offset: 2 }}>
                  <ServiceThreadSpinner on={!loaded}>
                     {data && data.length ? (
                        <PackingInquiryInvoiceGrid
                           data={data}
                           totals={totals}
                           sortCallback={onSortChange}
                           sortField={sortField}
                           sortDirection={sortDirection}
                           pagerCallback={onPageChange}
                           pageNumber={page}
                           totalPages={totalPages}
                           sizeCallback={onPageSizeChange}
                           pageSize={pageSize}
                           exportXlsxCallback={exportXlsx}
                           downloading={downloading}
                        />
                     ) : (
                        <div className="h5 text-center ">
                           {emptyCriteria
                              ? "Please fill in one or more criteria"
                              : "No Invoices found"}
                        </div>
                     )}
                  </ServiceThreadSpinner>
               </Col>
            </Row>
         </>
      );
   } else {
      return (
         <DesktopContainer screenName={`Invoice And Packing List Inquiry`}>
            <div className="filter-header mb-3">
               <Row className="justify-content-center">
                  <Col xs={5}>
                     {!isCustomer && (
                        <>
                           <Row className="align-items-center mt-2">
                              <Col xs={3}>
                                 <h5 children="Customer:" className="float-end text-white" />
                              </Col>
                              <Col xs={9}>
                                 <CustomerTypeahead
                                    value={customer}
                                    onChange={(e) => {
                                       setCustomer(e);
                                       setPage(1);
                                       setLoaded(false);
                                    }}
                                 />
                              </Col>
                           </Row>
                           <Row className="align-items-center mt-2">
                              <Col xs={3}>
                                 <h5 children="Salesperson:" className="float-end text-white" />
                              </Col>
                              <Col xs={9}>
                                 <FilterSelect
                                    filterName="SalespersonId"
                                    options={salespeopleList}
                                    value={filterHelpers.getValue(filters, "SalespersonId")}
                                    onChangeCallback={(e) => onFilterChange(e)}
                                    isClearable={
                                       userCtx.currentUser &&
                                       (userCtx.currentUser.isAdmin ||
                                          userCtx.currentUser.isPowerUser ||
                                          userCtx.currentUser.isCSRRole ||
                                          userCtx.currentUser.isSalesRole)
                                    }
                                 />
                              </Col>
                           </Row>
                        </>
                     )}
                     <Row className="align-items-center mt-2">
                        <Col xs={3}>
                           <h5 children="Invoice:" className="float-end text-white" />
                        </Col>
                        <Col xs={9}>
                           <FilterText
                              customFilterClassName="p-0"
                              filterName="OrderId"
                              value={filterHelpers.getValue(filters, "OrderId")}
                              onChangeCallback={onFilterChange}
                           />
                        </Col>
                     </Row>
                     <Row className="align-items-center mt-2">
                        <Col xs={3}>
                           <h5 children="PO:" className="float-end text-white" />
                        </Col>
                        <Col xs={9}>
                           <FilterText
                              customFilterClassName="p-0"
                              filterName="customerPONumber"
                              value={filterHelpers.getValue(filters, "customerPONumber")}
                              onChangeCallback={onFilterChange}
                           />
                        </Col>
                     </Row>
                     <Row className="align-items-center mt-2">
                        <Col xs={3}>
                           <h5 children="Case:" className="float-end text-white" />
                        </Col>
                        <Col xs={9}>
                           <FilterText
                              customFilterClassName="p-0"
                              filterName="LotNumber"
                              value={filterHelpers.getValue(filters, "LotNumber")}
                              onChangeCallback={onFilterChange}
                           />
                        </Col>
                     </Row>
                     <Row className="align-items-center mt-2">
                        <Col xs={3}>
                           <h5 children="Item:" className="float-end text-white" />
                        </Col>
                        <Col xs={9}>
                           {!isCustomer ? (
                              <FilterText
                                 customFilterClassName="p-0"
                                 filterName="ItemNumber"
                                 value={filterHelpers.getValue(filters, "ItemNumber")}
                                 onChangeCallback={onFilterChange}
                              />
                           ) : (
                              <FilterSelect
                                 filterName="ItemNumber"
                                 options={customerItems}
                                 value={filterHelpers.getValue(filters, "ItemNumber")}
                                 onChangeCallback={onFilterChange}
                                 isClearable
                              />
                           )}
                        </Col>
                     </Row>
                  </Col>
                  <Col xs={5}>
                     {!isCustomer && (
                        <Row className="align-items-center mt-2">
                           <Col xs={3}>
                              <h5 children="Bill To City:" className="float-end text-white" />
                           </Col>
                           <Col xs={9}>
                              <FilterSelect
                                 filterName="BTCity"
                                 options={billToCityList}
                                 value={filterHelpers.getValue(filters, "BTCity")}
                                 onChangeCallback={onFilterChange}
                                 isClearable
                              />
                           </Col>
                        </Row>
                     )}
                     <Row className="align-items-center mt-2">
                        <Col xs={3}>
                           <h5 children="Ship To City:" className="float-end text-white" />
                        </Col>
                        <Col xs={9}>
                           <FilterSelect
                              filterName="STCity"
                              options={shipToCityList}
                              value={filterHelpers.getValue(filters, "STCity")}
                              onChangeCallback={onFilterChange}
                              isClearable
                           />
                        </Col>
                     </Row>
                     <Row className="align-items-center mt-2">
                        <Col xs={3}>
                           <h5 children="Start Ship Date:" className="float-end text-white" />
                        </Col>
                        <Col xs={9}>
                           <FilterDateTime
                              filterName="StartDate"
                              value={filterHelpers.getValue(filters, "StartDate")}
                              onChangeCallback={onFilterChange}
                           />
                        </Col>
                     </Row>
                     <Row className="align-items-center mt-2">
                        <Col xs={3}>
                           <h5 children="End Ship Date:" className="float-end text-white" />
                        </Col>
                        <Col xs={9}>
                           <FilterDateTime
                              filterName="EndDate"
                              value={filterHelpers.getValue(filters, "EndDate")}
                              onChangeCallback={onFilterChange}
                           />
                        </Col>
                     </Row>
                     <Row className="align-items-center mt-2">
                        <Col xs={3}>
                           <h5 children="Order Type:" className="float-end text-white" />
                        </Col>

                        <Col xs={9}>
                           <FilterSelect
                              filterName="OrderType"
                              options={constants.ORDER_TYPES}
                              value={filterHelpers.getValue(filters, "OrderType")}
                              onChangeCallback={onFilterChange}
                              isClearable={false}
                           />
                        </Col>
                     </Row>
                  </Col>
               </Row>
            </div>
            <ServiceThreadSpinner on={!loaded}>
               {data && data.length ? (
                  <PackingInquiryInvoiceGrid
                     data={data}
                     totals={totals}
                     sortCallback={onSortChange}
                     sortField={sortField}
                     sortDirection={sortDirection}
                     pagerCallback={onPageChange}
                     pageNumber={page}
                     totalPages={totalPages}
                     sizeCallback={onPageSizeChange}
                     pageSize={pageSize}
                     exportXlsxCallback={exportXlsx}
                     downloading={downloading}
                  />
               ) : (
                  <div className="h5 text-center">
                     {emptyCriteria
                        ? "Please fill in one or more criteria"
                        : "No Invoices found"}
                  </div>
               )}
            </ServiceThreadSpinner>
         </DesktopContainer>
      );
   }
}
